import { createInertiaApp, router } from "@inertiajs/react";
import { createRoot } from "react-dom/client";
import type { ComponentType } from "react";

import { ErrorBoundary } from "~/components/ErrorBoundary";
import { Toaster } from "~/components/Toaster";
import { announceFailure } from "~/lib/errorFeedback";
import "~/styles/application.css";

type PageModule = { default: ComponentType<Record<string, unknown>> };

// Lazy: each page is its own chunk, fetched when it is first visited (audit F-4.7).
// This was `{ eager: true }`, which put every page — the back office, the till,
// analytics — into the one entry script, so a stranger opening a join link on
// prepaid data downloaded the whole product before the form could paint.
//
// Two things make lazy safe here. After a deploy, Inertia's asset version check
// answers a stale client with a full reload *before* it resolves the next page,
// so an old page is never asked for a chunk the new build removed. And the till's
// service worker (`public/scanner-sw.js`) caches `/vite/` assets at runtime by
// prefix, so a page chunk is cached exactly the way the single bundle was.
const pages = import.meta.glob<PageModule>("../pages/**/*.tsx");

void createInertiaApp({
  resolve: async (name: string) => {
    const load = pages[`../pages/${name}.tsx`];
    if (!load) {
      throw new Error(`Inertia page not found: ${name}`);
    }
    return (await load()).default;
  },
  setup({ el, App, props }) {
    // Every page renders inside the error boundary, so an unexpected render
    // crash degrades to a reload prompt rather than a blank screen. The toaster
    // sits beside the page rather than in a layout, so every surface — back
    // office, sign-in, the customer's join form — gets it without asking.
    createRoot(el).render(
      <ErrorBoundary>
        <App {...props} />
        <Toaster />
      </ErrorBoundary>,
    );
  },
});

// An Inertia form (`useForm`) that came back with validation errors: the same
// treatment `apiFetch` gives a failed write.
router.on("error", (event) => {
  if (Object.keys(event.detail.errors).length > 0) announceFailure();
});

// `<html lang>` is rendered once, on the first full load; a visit changes the
// page but not the document. Keeping it in step with the server's `locale` is
// right for screen readers, and it is what the toaster — outside the page tree —
// reads its own two strings from.
router.on("navigate", (event) => {
  const { locale } = event.detail.page.props as { locale?: unknown };
  if (typeof locale === "string" && locale) document.documentElement.lang = locale;
});
